import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock } from "lucide-react";
import Navbar from "@/components/portfolio/Navbar";
import Contact from "@/components/portfolio/Contact";
import Comments from "@/components/blog/Comments";
import api from "@/lib/api";

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [all, setAll] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    let active = true;
    api
      .get(`/posts/${slug}`)
      .then(({ data }) => {
        if (!active) return;
        setPost(data);
        document.title = `${data.title} — Ali Renan Uyanık`;
      })
      .catch(() => active && setNotFound(true));
    api
      .get("/posts")
      .then(({ data }) => active && setAll(data))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [slug]);

  if (notFound) {
    return (
      <main className="min-h-screen bg-black text-white">
        <Navbar />
        <section className="pt-40 pb-32 max-w-3xl mx-auto px-6 text-center">
          <div className="text-sm text-zinc-500">404</div>
          <h1 className="font-heading mt-3 text-4xl md:text-5xl font-light tracking-tight">
            Post not found.
          </h1>
          <Link
            to="/blog"
            className="mt-8 inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-medium hover:scale-[1.04] transition-transform"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            All posts
          </Link>
        </section>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="min-h-screen bg-black text-white">
        <Navbar />
        <section className="pt-40 text-center text-zinc-500 text-sm">
          Loading post…
        </section>
      </main>
    );
  }

  const more = all.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <main
      data-testid="blog-post-root"
      className="relative bg-black text-white min-h-screen"
    >
      <Navbar />

      <article className="relative pt-28 md:pt-32 pb-14 md:pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link
            to="/blog"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            All posts
          </Link>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mt-8 flex items-center gap-3 text-sm text-zinc-500"
          >
            <span>{post.category || "Post"}</span>
            <span className="w-1 h-1 rounded-full bg-zinc-700" />
            <span>{fmt(post.date)}</span>
            {post.read_time && (
              <>
                <span className="w-1 h-1 rounded-full bg-zinc-700" />
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-3 h-3" strokeWidth={1.5} />
                  {post.read_time}
                </span>
              </>
            )}
          </motion.div>

          <motion.h1
            data-testid="blog-post-title"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading mt-4 text-3xl md:text-5xl lg:text-[56px] font-light tracking-[-0.03em] leading-[1.05] text-white"
          >
            {post.title}
          </motion.h1>

          {post.cover && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-10 relative aspect-[16/9] overflow-hidden rounded-2xl border border-white/10"
            >
              <img
                src={post.cover}
                alt={post.title}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </motion.div>
          )}

          <div className="prose prose-invert mt-10 max-w-none">
            {(post.body || []).map((block, i) => {
              const img =
                typeof block === "string" &&
                block.trim().match(/^!\[(.*?)\]\((.*?)\)$/);
              if (img) {
                const caption = img[1];
                const src = img[2];
                return (
                  <motion.figure
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6 }}
                    className="my-10 first:mt-0"
                    data-testid={`blog-image-${i}`}
                  >
                    <div className="relative overflow-hidden rounded-2xl border border-white/10">
                      <img
                        src={src}
                        alt={caption || ""}
                        loading="lazy"
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    {caption && (
                      <figcaption className="mt-3 text-sm text-zinc-500 text-center">
                        {caption}
                      </figcaption>
                    )}
                  </motion.figure>
                );
              }
              return (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.03 }}
                  className="text-zinc-300 font-light text-lg leading-[1.85] mt-6 first:mt-0"
                  data-testid={`blog-paragraph-${i}`}
                >
                  {block}
                </motion.p>
              );
            })}
          </div>

          <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
            <div>
              <div className="text-sm text-zinc-500">Written by</div>
              <div className="mt-1 font-heading text-xl text-white">
                Ali Renan Uyanık
              </div>
            </div>
            <Link
              to="/blog"
              data-testid="blog-post-back-bottom"
              className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-medium hover:scale-[1.04] transition-transform"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
              Back to all posts
            </Link>
          </div>
        </div>
      </article>

      <Comments slug={slug} />

      {more.length > 0 && (
        <section className="py-16 md:py-20 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex items-center gap-3 text-sm text-zinc-500 mb-6">
              <span className="w-8 h-px bg-zinc-700" />
              <span>Keep reading</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {more.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-more-${p.slug}`}
                  className="group block rounded-2xl border border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04] transition-colors overflow-hidden"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    {p.cover && (
                      <img
                        src={p.cover}
                        alt={p.title}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover grayscale-[55%] group-hover:grayscale-0 scale-[1.02] group-hover:scale-105 transition-all duration-[1200ms] ease-out"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  </div>
                  <div className="p-6">
                    <div className="text-sm text-zinc-500">
                      {(p.category || "Post")} · {fmt(p.date)}
                    </div>
                    <h3 className="font-heading mt-2 text-xl md:text-2xl font-medium tracking-tight text-white">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Contact />
    </main>
  );
}
